import {
  Component,
  computed,
  inject,
  Input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Task } from '@modules/sales/models/task.model';
import { KfmContactCardComponent } from '@modules/contact/features/contact-card/kfm-contact-card.component';
import { KfmShoppingCartComponent } from '@modules/shopping/features/shopping-cart/kfm-shopping-cart.component';
import { KfmPrintSettingsCardComponent } from '@modules/settings/features/print-settings-card/kfm-print-settings-card.component';
import { KfmSalesDetailsCardComponent } from '../sales-details-card/kfm-sales-details-card.component';
import { KfmReportCardComponent } from '../../../../report/features/report-card/kfm-report-card.component';
import { Contact } from '@modules/contact/models/contact.model';
import { KfmStatusHistoryCardComponent } from '../../../../status-history/features/status-history-card/kfm-status-history-card.component';
import {
  ICON_CANCEL,
  ICON_CONVERT,
  ICON_DELETE,
  ICON_DOWNLOAD,
  ICON_FLAG,
  ICON_LOCATION,
  ICON_PRINT,
  ICON_USER,
} from '@shared/ui/icons/icon-constants';
import { DomSanitizer } from '@angular/platform-browser';
import { AppointmentService } from '@modules/sales/services/appointment.service';
import { TaskService } from '@modules/sales/services/task.service';
import { InvoiceService } from '@modules/sales/services/invoice.service';
import { OfferService } from '@modules/sales/services/offer.service';
import { TaskTypeService } from '@modules/sales/services/task-type.service';
import { ConvertSalesItemService } from '@modules/sales/services/convert-sales-item.service';
import { KaufmannPrintSettingsService } from '@shared/settings/print-settings.service';
import {
  HsButtonModule,
  HsIconComponent,
  HsTabGroupComponent,
  HsTabComponent,
  HsTooltipModule,
} from '@hsetu/ngui-components';
import { KfmAppointmentDateCardComponent } from '@modules/sales/features/appointments/appointment-date-card/kfm-appointment-date-card.component';
import { History } from '@modules/status-history/models/history.model';
import { PrintSettings } from '@shared/settings/print-settings.model';
import { Dialog } from '@angular/cdk/dialog';
import { KfmProcessType } from '@shared/enums/process-type.enum';
import { PrintService } from '@shared/tools/print.service';
import { KaufmannSettingsService } from '@modules/settings/services/kfm-settings.service';
import {
  STATUS_ABGESCHLOSSEN,
  STATUS_DURCHGEFUEHRT,
  STATUS_GEBUCHT,
  STATUS_STORNIERT,
} from '@modules/status-history/constants/status-constants';
import { KfmSalesDetailsEvents } from './kfm-sales-details-events';
import { KfmSignatureCardComponent } from '@shared/ui/components/features/signature/signature-card/kfm-signature-card.component';
import { KfmYesNoDialogComponent } from '@shared/ui/components/features/dialogs/yes-no-dialog/kfm-yes-no-dialog.component';
import { KfmInfoDialogComponent } from '@shared/ui/components/features/dialogs/info-dialog/kfm-info-dialog.component';
import { KfmPrintDialogComponent } from '@shared/ui/components/features/dialogs/print-dialog/kfm-print-dialog.component';
import { KfmMediaGalleryComponent } from '@modules/media/features/kfm-media-gallery/kfm-media-gallery.component';
import { GalleryItem } from '@modules/media/models/gallery-item.model';
import { KfmImageType } from '@modules/media/enums/kfm-image-type.enum';
import { KfmSpinnerDialogComponent } from '@shared/ui/components/features/dialogs/spinner-dialog/kfm-spinner-dialog.component';
import {
  BOOK_INVOICE_BUTTON_LABEL,
  BOOK_INVOICE_SAVING_ITEMS_TOOLTIP_LABEL,
  BOOK_INVOICE_ERROR_TITLE_LABEL,
  BOOK_INVOICE_ERROR_TEXT_LABEL,
  BOOK_INVOICE_LABEL,
  BOOK_INVOICE_WARN_TITLE_LABEL,
  CANCEL_INVOICE_LABEL,
  CONVERT_OFFER,
  CONVERT_TO_INVOICE,
  CONVERT_TASK,
  CUSTOMER_LABEL,
  CUSTOMER_MEDIA_LABEL,
  CUSTOMER_SIGNATURE_LABEL,
  DELETE_APPOINTMENT_LABEL,
  DELETE_TASK_LABEL,
  DELETE_TASK_TEXT_LABEL,
  DELETE_APPOINTMENT_TEXT_LABEL,
  DELETE_INVOICE_LABEL,
  DELETE_INVOICE_TEXT_LABEL,
  DELETE_OFFER_LABEL,
  DELETE_OFFER_TEXT_LABEL,
  DETAILS_LABEL,
  DOWNLOAD_DOCUMENT_LABEL,
  FINISH_TASK_LABEL,
  FINISH_TASK_WARN_TITLE_LABEL,
  LOCATION_LABEL,
  LOCATION_MEDIA_LABEL,
  MECHANIC_SIGNATURE_LABEL,
  MEDIA_LABEL,
  PRINT_APPOINTMENT_LABEL,
  PRINT_INVOICE_LABEL,
  PRINT_OFFER_LABEL,
  PRINT_TASK_LABEL,
  PROCEED_LABEL,
  SALES_ITEM_MEDIA_LABEL,
} from '@modules/sales/i18n/label-text-constants';

@Component({
  selector: 'kfm-sales-details',
  imports: [
    CommonModule,
    KfmContactCardComponent,
    KfmShoppingCartComponent,
    KfmPrintSettingsCardComponent,
    KfmSalesDetailsCardComponent,
    KfmReportCardComponent,
    KfmStatusHistoryCardComponent,
    HsButtonModule,
    HsIconComponent,
    HsTooltipModule,
    KfmAppointmentDateCardComponent,
    KfmSignatureCardComponent,
    HsTabGroupComponent,
    HsTabComponent,
    HsTooltipModule,
    KfmMediaGalleryComponent,
  ],
  templateUrl: './kfm-sales-details.component.html',
  host: { class: 'kfm-sales-details' },
})
export class KfmSalesDetailsComponent {
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _taskService = inject(TaskService);
  private readonly _invoiceService = inject(InvoiceService);
  private readonly _offerService = inject(OfferService);
  private readonly _taskTypeService = inject(TaskTypeService);
  private readonly _appointmentService = inject(AppointmentService);
  private readonly _dialog = inject(Dialog);
  private readonly _printService = inject(PrintService);
  private readonly _settings = inject(KaufmannSettingsService);
  private readonly _processConvertService = inject(ConvertSalesItemService);
  private readonly _printSettingsService = inject(KaufmannPrintSettingsService);

  private readonly _salesDetailsEvents = new KfmSalesDetailsEvents();

  private isDialogOpen = false;

  @ViewChild(HsTabGroupComponent)
  private readonly _tabGroup!: HsTabGroupComponent;

  @ViewChild(KfmShoppingCartComponent)
  private readonly _shoppingCart?: KfmShoppingCartComponent;

  protected isShoppingCartSaving(): boolean {
    return this._shoppingCart?.isSaving() ?? false;
  }

  protected readonly salesItemMediaLabel = SALES_ITEM_MEDIA_LABEL;
  protected readonly customerCardTitle = CUSTOMER_LABEL;
  protected readonly locationCardTitle = LOCATION_LABEL;
  protected readonly customerMediaLabel = CUSTOMER_MEDIA_LABEL;
  protected readonly locationMediaLabel = LOCATION_MEDIA_LABEL;
  protected readonly bookInvoiceWarningTitle = BOOK_INVOICE_WARN_TITLE_LABEL;
  protected readonly proceedLabel = PROCEED_LABEL;
  protected readonly finishTaskWarningTitle = FINISH_TASK_WARN_TITLE_LABEL;
  protected readonly bookInvoiceLabel = BOOK_INVOICE_LABEL;
  protected readonly detailsLabel = DETAILS_LABEL;
  protected readonly mediaLabel = MEDIA_LABEL;
  protected readonly customerSignatureLabel = CUSTOMER_SIGNATURE_LABEL;
  protected readonly mechanicSignatureLabel = MECHANIC_SIGNATURE_LABEL;
  protected readonly finishTaskLabel = FINISH_TASK_LABEL;
  protected readonly bookInvoiceButtonLabel = BOOK_INVOICE_BUTTON_LABEL;
  protected readonly savingItemsTooltip =
    BOOK_INVOICE_SAVING_ITEMS_TOOLTIP_LABEL;
  protected readonly bookInvoiceErrorTitle = BOOK_INVOICE_ERROR_TITLE_LABEL;
  protected readonly bookInvoiceErrorText = BOOK_INVOICE_ERROR_TEXT_LABEL;
  protected readonly deleteLabel = DELETE_APPOINTMENT_LABEL;
  protected readonly printLabel = PRINT_APPOINTMENT_LABEL;
  protected readonly cancelInvoiceLabel = CANCEL_INVOICE_LABEL;
  protected readonly deleteInvoiceLabel = DELETE_INVOICE_LABEL;
  protected readonly printInvoiceLabel = PRINT_INVOICE_LABEL;
  protected readonly printOfferLabel = PRINT_OFFER_LABEL;
  protected readonly convertInvoiceLabel = CONVERT_TO_INVOICE;
  protected readonly convertOfferLabel = CONVERT_OFFER;
  protected readonly deleteOfferLabel = DELETE_OFFER_LABEL;
  protected readonly downloadDocumentLabel = DOWNLOAD_DOCUMENT_LABEL;
  protected readonly deleteTaskLabel = DELETE_TASK_LABEL;
  protected readonly deleteTaskTextLabel = DELETE_TASK_TEXT_LABEL;
  protected readonly printTaskLabel = PRINT_TASK_LABEL;
  protected readonly convertTaskLabel = CONVERT_TASK;

  protected readonly deleteIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_DELETE);
  protected readonly printIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_PRINT);
  protected readonly convertIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_CONVERT);
  protected readonly downloadIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_DOWNLOAD);
  protected readonly cancelIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_CANCEL);

  protected readonly internalSalesItem = signal<Task | undefined>(undefined);

  /**
   * True when the currently loaded invoice has an original (non-storno) booking.
   * Drives the visibility of the "Buchen" and "Stornieren" buttons.
   * Loaded asynchronously whenever a new invoice is set via [salesItem].
   */
  protected readonly hasActiveBooking = signal<boolean>(false);

  /** Show or hide the status history card depending on the sales item type */
  protected readonly showStatusHistoryCard = computed(() => {
    const saleItem = this.internalSalesItem();

    if (!saleItem || !saleItem.type) return false;

    if (saleItem.type === 0 || saleItem.type === 1) {
      // Check if appointment or task
      if (this._taskTypeService.isAppointment(saleItem)) {
        return false;
      }
    } else if (saleItem.type === 2) {
      // Offer
      return false;
    }

    if (saleItem.isTemplate === true) {
      return false;
    }

    return true;
  });

  protected readonly showCustomerCard = computed(() => {
    const saleItem = this.internalSalesItem();

    if (!saleItem) return false;

    if (saleItem.type == null && !this._taskTypeService.isAppointment(saleItem))
      return false;

    if (saleItem.isTemplate === true) return false;

    return true;
  });

  /** Show or hide the shopping card depending on the sales item type */
  protected readonly showShoppingCard = computed(() => {
    const saleItem = this.internalSalesItem();

    if (!saleItem || !saleItem.type) return false;

    if (this._taskTypeService.isAppointment(saleItem)) {
      return false;
    }

    if (saleItem.isTemplate === true) return false;

    return true;
  });

  /** Show or hide the report card depending on the sales item type */
  protected readonly showReportCard = computed(() => {
    const saleItem = this.internalSalesItem();

    if (!saleItem || !saleItem.type) return false;

    if (
      (saleItem.type === 0 || saleItem.type === 1) &&
      !this._taskTypeService.isAppointment(saleItem)
    ) {
      return true;
    }

    return false;
  });

  protected readonly customerCardIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_USER);

  protected readonly customer = computed(() => {
    const salesItem = this.internalSalesItem();

    if (salesItem) return salesItem.customer;
  });

  protected readonly locationCardIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_LOCATION);

  protected readonly finishIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_FLAG);

  /** Get the location address from the sales item */
  protected readonly location = computed(() => {
    const salesItem = this.internalSalesItem();

    if (salesItem) return salesItem.location;
  });

  /** Get the report from the sales item */
  protected readonly report = computed(() => {
    const salesItem = this.internalSalesItem();

    if (salesItem) return salesItem.report;
  });

  /** Get the customer signature from the sales item */
  protected readonly customerSignature = computed(() => {
    const salesItem = this.internalSalesItem();

    if (
      salesItem &&
      salesItem.customerSignature &&
      salesItem.customerSignature.content
    ) {
      const mimeType = salesItem.customerSignature.mimeType || '';
      const content = salesItem.customerSignature.content;

      if (content.includes(mimeType)) {
        return content;
      }

      return 'data:' + mimeType + ';base64,' + content;
    }

    return null;
  });

  /** Get the mechanic signature from the sales item */
  protected readonly mechanicSignature = computed(() => {
    const salesItem = this.internalSalesItem();

    if (
      salesItem &&
      salesItem.mechanicSignature &&
      salesItem.mechanicSignature.content
    ) {
      const mimeType = salesItem.mechanicSignature.mimeType || '';
      const content = salesItem.mechanicSignature.content;
      return 'data:' + mimeType + ';base64,' + content;
    }

    return null;
  });

  /** Check if the sales item is a task */
  protected readonly isTask = computed(() => {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return false;

    return this._taskTypeService.isTask(salesItem);
  });

  /** Check if the sales item is an offer */
  protected readonly isOffer = computed(() => {
    const salesItem = this.internalSalesItem();
    if (!salesItem) return false;
    return this._taskTypeService.isOffer(salesItem);
  });

  /** Check if the sales item is an invoice */
  protected readonly isInvoice = computed(() => {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return false;

    return this._taskTypeService.isInvoice(salesItem);
  });

  protected readonly isTemplate = computed(() => {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return false;

    return this._taskTypeService.isTemplate(salesItem);
  });

  /** Check if the sales item is an appointment */
  protected readonly isAppointment = computed(() => {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return false;

    return this._taskTypeService.isAppointment(salesItem);
  });

  protected readonly statusHistory = computed<History>(() => {
    const salesItem = this.internalSalesItem();

    if (!salesItem) {
      return {
        ownerId: '',
        ownerType: 1,
        stati: [],
      };
    }

    if (!salesItem.statusHistory || salesItem.statusHistory.length === 0) {
      return {
        ownerId: salesItem.uId,
        ownerType: salesItem.type ?? 1,
        stati: [],
      };
    } else {
      return {
        ownerId: salesItem.uId,
        ownerType: salesItem.type ?? 1,
        stati: salesItem.statusHistory,
      };
    }
  });

  /** Check if the sales item is editable */
  protected readonly isEditable = computed(() => {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return false;

    if (salesItem.isTemplate === true) return true;

    if (
      this._taskTypeService.isInvoice(salesItem) &&
      salesItem.statusHistory &&
      salesItem.statusHistory.length > 0
    ) {
      const booked =
        salesItem.statusHistory[0].statusId?.toUpperCase() === STATUS_GEBUCHT;

      const canceled =
        salesItem.statusHistory[0].statusId?.toUpperCase() === STATUS_STORNIERT;

      return !booked && !canceled;
    } else if (
      this._taskTypeService.isTask(salesItem) &&
      salesItem.statusHistory &&
      salesItem.statusHistory.length > 0
    ) {
      const closed =
        salesItem.statusHistory[0].statusId?.toUpperCase() ===
        STATUS_ABGESCHLOSSEN;
      const done =
        salesItem.statusHistory[0].statusId?.toUpperCase() ===
        STATUS_DURCHGEFUEHRT;

      return !closed && !done;
    }

    return !this._taskTypeService.isInvoice(salesItem);
  });

  /** Check if invoice is booked */
  protected readonly isBooked = computed(() => {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return false;

    if (
      this._taskTypeService.isInvoice(salesItem) &&
      salesItem.statusHistory &&
      salesItem.statusHistory.length > 0
    ) {
      return (
        salesItem.statusHistory[0].statusId?.toUpperCase() === STATUS_GEBUCHT
      );
    }

    return false;
  });

  /** Check if invoice is cancelled */
  protected readonly isCancelled = computed(() => {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return false;

    if (
      this._taskTypeService.isInvoice(salesItem) &&
      salesItem.statusHistory &&
      salesItem.statusHistory.length > 0
    ) {
      return (
        salesItem.statusHistory[0].statusId?.toUpperCase() === STATUS_STORNIERT
      );
    }

    return false;
  });

  protected readonly galleries = computed(() => {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return new Map<string, GalleryItem>();

    const galleries = new Map<string, GalleryItem>();

    let imageType = KfmImageType.Foto_zum_Auftrag;

    if (this._taskTypeService.isInvoice(salesItem)) {
      imageType = KfmImageType.Foto_zur_Rechnung;
    } else if (this._taskTypeService.isOffer(salesItem)) {
      imageType = KfmImageType.Foto_zum_Auftrag;
    } else if (this._taskTypeService.isAppointment(salesItem)) {
      imageType = KfmImageType.Foto_zum_Termin;
    }

    galleries.set(salesItem.uId, {
      title: this.salesItemMediaLabel,
      type: imageType,
      dataSource: null,
    });

    if (salesItem.customer) {
      galleries.set(salesItem.customer.uId, {
        title: this.customerMediaLabel,
        type: KfmImageType.Foto_zum_Kontakt,
        dataSource: null,
      });
    }

    if (salesItem.location) {
      galleries.set(salesItem.location.uId, {
        title: this.locationMediaLabel,
        type: KfmImageType.Foto_zum_Standort,
        dataSource: null,
      });
    }

    return galleries;
  });

  @Input({ required: true })
  public set salesItem(value: Task | undefined) {
    this.internalSalesItem.set(value);
    // Always return to the details tab when a new item is selected
    this._tabGroup?.selectTab(0);
    // Reset and then asynchronously reload the booking state for the new invoice
    this.hasActiveBooking.set(false);
    if (value && this._taskTypeService.isInvoice(value)) {
      this._invoiceService.getBookingForInvoice(value.uId).then((booking) => {
        this.hasActiveBooking.set(booking !== null);
      });
    }
  }

  /** True when the currently displayed invoice came from the booked+cancelled tab. */
  @Input() public isBookedInvoice = false;

  public salesItemDeselected = output<void>();
  public salesItemUpdated = output<Task>();
  public salesItemBooked = output<Task>();
  public salesListRefresh = output<void>();
  public salesItemDeleted = output<void>();
  public calendarReloadRequired = output<void>();

  /** EVENTS */

  protected async onAppointmentChanged(): Promise<void> {
    this.calendarReloadRequired.emit();
  }

  protected onTaskChanged(task: Task): void {
    this.internalSalesItem.set(task);
    this.salesItemUpdated.emit(task);
  }

  protected onSalesItemChanged(updatedItem: Task): void {
    const merged = { ...this.internalSalesItem(), ...updatedItem };
    this.internalSalesItem.set(merged);
    this.salesItemUpdated.emit(merged);
  }

  protected onDeleteAppointmentClicked(): void {
    if (this.isDialogOpen) return;
    this.isDialogOpen = true;
    const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        title: this.deleteLabel,
        text: DELETE_APPOINTMENT_TEXT_LABEL,
      },
    });
    dialogRef.closed.subscribe(async (confirmed) => {
      this.isDialogOpen = false;
      if (!confirmed) return;
      const salesItem = this.internalSalesItem();

      if (!salesItem || !salesItem.appointment) return;

      const ok = await this._appointmentService.delete(
        salesItem.appointment.uId,
      );
      if (ok) {
        this.salesItemDeselected.emit();
        this.calendarReloadRequired.emit();
      }
    });
  }

  protected async onPrintAppointmentClicked(): Promise<void> {
    const salesItem = this.internalSalesItem();

    if (!salesItem || !salesItem.appointment) return;

    const name =
      salesItem.appointment.subject ?? salesItem.infoDesc ?? 'Termin';
    await this._printService.createAppointmentPdf(
      salesItem.appointment.uId,
      false,
      `Termin_${name}.pdf`,
    );
  }

  protected async onCustomerAdded(contact: Contact) {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return;

    await this._salesDetailsEvents.onCustomerAdded(salesItem, contact);
    this.internalSalesItem.set({ ...salesItem });
  }

  protected async onCustomerRemoved() {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return;

    await this._salesDetailsEvents.onCustomerRemoved(salesItem);
    this.internalSalesItem.set({ ...salesItem });
  }

  protected async onLocationAdded(contact: Contact) {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return;

    await this._salesDetailsEvents.onLocationAdded(salesItem, contact);
    this.internalSalesItem.set({ ...salesItem });
  }

  protected async onLocationRemoved() {
    const salesItem = this.internalSalesItem();

    if (!salesItem || !salesItem.appointment) return;

    if (this._taskTypeService.isAppointment(salesItem)) {
      salesItem.appointment.location = undefined;
      salesItem.location = undefined;
      await this._appointmentService.update(
        salesItem.appointment,
        undefined,
        salesItem.appointment.customer?.uId,
        undefined,
      );
    } else {
      salesItem.location = undefined;
      if (this._taskTypeService.isTask(salesItem)) {
        await this._taskService.update(salesItem);
      } else if (this._taskTypeService.isInvoice(salesItem)) {
        await this._invoiceService.update(salesItem);
      }
    }
    this.internalSalesItem.set({ ...salesItem });
  }

  protected onStatusHistoryChanged(history: History) {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return;

    salesItem.statusHistory = history.stati;
  }

  protected async onBookInvoiceClicked() {
    if (this.isShoppingCartSaving()) {
      return;
    }

    // Check if permentTerm is selected
    const salesItem = this.internalSalesItem();

    if (!salesItem) return;

    const warn: { isError: boolean; warnMsg?: string } =
      await this._salesDetailsEvents.onBookInvoiceClicked(salesItem);

    if (warn.isError) {
      return;
    }

    if (this.isDialogOpen) {
      return;
    }

    if (warn.warnMsg) {
      this.isDialogOpen = true;

      const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          title: this.bookInvoiceWarningTitle,
          text: warn.warnMsg + this.proceedLabel,
        },
      });

      dialogRef.closed.subscribe((proceed) => {
        if (proceed) {
          this.bookInvoice();
        }

        this.isDialogOpen = false;
      });
    } else {
      this.bookInvoice();
    }
  }

  protected async onFinishTaskClicked() {
    const activeElement = document.activeElement as HTMLElement | null;
    activeElement?.blur();

    // Let blur/change handlers update the bound sales item before validation runs.
    await Promise.resolve();

    const salesItem = this.internalSalesItem();

    if (!salesItem) return;

    const warn: { isError: boolean; warnMsg?: string } =
      this._salesDetailsEvents.onFinishTaskClicked(salesItem);

    if (warn.isError) {
      return;
    }

    if (this.isDialogOpen) {
      return;
    }

    if (warn.warnMsg) {
      this.isDialogOpen = true;

      const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          title: this.finishTaskWarningTitle,
          text: warn.warnMsg + this.proceedLabel,
        },
      });

      dialogRef.closed.subscribe((proceed) => {
        if (proceed) {
          this.finishTask();
        }

        this.isDialogOpen = false;
      });
    } else {
      this.finishTask();
    }
  }

  protected async onMechanicSignatureChanged(dataUrl: string | null) {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return;

    if (this._taskTypeService.isTask(salesItem)) {
      await this._salesDetailsEvents.onMechanicSignatureChanged(
        salesItem,
        dataUrl,
      );
    }
  }

  protected async onCustomerSignatureChanged(dataUrl: string | null) {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return;

    if (this._taskTypeService.isTask(salesItem)) {
      await this._salesDetailsEvents.onCustomerSignatureChanged(
        salesItem,
        dataUrl,
      );
    }
  }

  protected async onPrintClicked(): Promise<void> {
    const salesItem = this.internalSalesItem();
    if (!salesItem) return;

    if (this.isInvoice() && (this.isBooked() || this.isCancelled())) {
      const printSettings = this.getMetaPrintSettings(salesItem);
      if (printSettings) {
        const spinnerRef = this._dialog.open(KfmSpinnerDialogComponent, {
          data: { text: 'PDF wird erstellt...' },
        });
        try {
          await this._printService.createInvoicePdf(
            salesItem,
            printSettings,
            this._settings.showPrice,
          );
        } finally {
          spinnerRef.close();
        }
        return;
      }
    }

    if (this.isDialogOpen) return;
    this.isDialogOpen = true;
    this.showPrintDialog(salesItem, false);
  }

  protected async onConvertClicked(): Promise<void> {
    const salesItem = this.internalSalesItem();
    if (!salesItem) return;
    await this._processConvertService.convertProcess(salesItem);
  }

  protected async onDownloadInvoiceClicked(): Promise<void> {
    const salesItem = this.internalSalesItem();
    if (!salesItem) return;

    const printSettings = this.getMetaPrintSettings(salesItem);
    if (printSettings) {
      const spinnerRef = this._dialog.open(KfmSpinnerDialogComponent, {
        data: { text: 'PDF wird erstellt...' },
      });
      try {
        await this._printService.createInvoicePdf(
          salesItem,
          printSettings,
          this._settings.showPrice,
          true,
          `Rechnung_${salesItem.number}.pdf`,
        );
      } finally {
        spinnerRef.close();
      }
    } else {
      if (this.isDialogOpen) return;
      this.isDialogOpen = true;
      this.showPrintDialog(salesItem, true);
    }
  }

  protected async onCancelInvoiceClicked(): Promise<void> {
    if (this.isDialogOpen) return;
    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        title: this.cancelInvoiceLabel,
        text: 'Wollen Sie die Rechnung wirklich stornieren?',
      },
    });

    dialogRef.closed.subscribe(async (confirmed) => {
      this.isDialogOpen = false;
      if (!confirmed) return;
      const salesItem = this.internalSalesItem();

      if (!salesItem) return;

      const result = await this._invoiceService.cancel(salesItem.uId);
      if (result) {
        // Update the list item locally with the storniert status before deselecting.
        // We intentionally skip salesListRefresh here: an immediate WASM reload may
        // return stale data (PutStatusHistory not yet propagated), which would
        // overwrite the local patch and hide the storniert badge.
        this.salesItemUpdated.emit(result);
        this.salesItemDeselected.emit();
      }
    });
  }

  protected async onDeleteClicked(): Promise<void> {
    const salesItem = this.internalSalesItem();
    if (!salesItem) return;

    if (this._taskTypeService.isAppointment(salesItem)) {
      this.onDeleteAppointmentClicked();
    } else if (this._taskTypeService.isTask(salesItem)) {
      this.onDeleteTaskClicked();
    } else if (this._taskTypeService.isOffer(salesItem)) {
      this.onDeleteOfferClicked();
    } else if (this._taskTypeService.isInvoice(salesItem)) {
      this.onDeleteInvoiceClicked();
    }
  }

  protected async onDeleteInvoiceClicked(): Promise<void> {
    if (this.isDialogOpen) return;
    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        title: this.deleteInvoiceLabel,
        text: DELETE_INVOICE_TEXT_LABEL,
      },
    });

    dialogRef.closed.subscribe(async (confirmed) => {
      this.isDialogOpen = false;
      if (!confirmed) return;
      const salesItem = this.internalSalesItem();

      if (!salesItem) return;

      const ok = await this._invoiceService.delete(salesItem.uId);
      if (ok) {
        this.salesItemDeselected.emit();
        this.salesListRefresh.emit();
      }
    });
  }

  protected async onDeleteOfferClicked(): Promise<void> {
    if (this.isDialogOpen) return;
    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        title: this.deleteOfferLabel,
        text: DELETE_OFFER_TEXT_LABEL,
      },
    });

    dialogRef.closed.subscribe(async (confirmed) => {
      this.isDialogOpen = false;
      if (!confirmed) return;
      const salesItem = this.internalSalesItem();

      if (!salesItem) return;

      const ok = await this._offerService.delete(salesItem.uId);
      if (ok) {
        this.salesItemDeselected.emit();
        this.salesListRefresh.emit();
      }
    });
  }

  /********* */

  private showPrintDialog(
    salesItem: Task,
    savePrintSettings: boolean,
    onPrinted?: (printSettings: PrintSettings) => Promise<void>,
  ): void {
    let processType = KfmProcessType.Invoice;
    if (this._taskTypeService.isTask(salesItem)) {
      processType = KfmProcessType.Order;
    } else if (this._taskTypeService.isOffer(salesItem)) {
      processType = KfmProcessType.Offer;
    }

    const dialogRef = this._dialog.open<PrintSettings>(
      KfmPrintDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: { processType },
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      try {
        if (result) {
          if (onPrinted) {
            await onPrinted(result);
          } else {
            const spinnerRef = this._dialog.open(KfmSpinnerDialogComponent, {
              data: { text: 'PDF wird erstellt...' },
            });
            try {
              await this.createPdf(salesItem, result);
            } finally {
              spinnerRef.close();
            }
          }

          if (savePrintSettings) {
            const meta =
              await this._printSettingsService.saveSettingsToDatabase(
                salesItem.uId,
                result,
              );
            if (meta) salesItem.meta = meta;
          }
        }
      } catch (err) {
        console.error('[SalesDetails] Fehler beim Drucken:', err);
        this._dialog.open(KfmInfoDialogComponent, {
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          data: {
            title: 'Fehler',
            text: 'Der Vorgang konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
          },
        });
      } finally {
        this.isDialogOpen = false;
      }
    });
  }

  private async createPdf(
    salesItem: Task,
    printSettings: PrintSettings,
  ): Promise<void> {
    const processType = this._taskTypeService.isTask(salesItem)
      ? KfmProcessType.Order
      : this._taskTypeService.isOffer(salesItem)
        ? KfmProcessType.Offer
        : KfmProcessType.Invoice;

    if (processType === KfmProcessType.Order) {
      await this._printService.createTaskPdf(
        salesItem.uId,
        printSettings,
        this._settings.showPrice,
      );
    } else if (processType === KfmProcessType.Offer) {
      await this._printService.createOfferPdf(
        salesItem.uId,
        printSettings,
        this._settings.showPrice,
      );
    } else {
      await this._printService.createInvoicePdf(
        salesItem,
        printSettings,
        this._settings.showPrice,
      );
    }
  }

  private getMetaPrintSettings(salesItem: Task): PrintSettings | null {
    const raw = salesItem.meta
      ? JSON.parse(salesItem.meta).PrintSettings
      : null;
    if (!raw) return null;
    return this._printSettingsService.mapPrintSettings(JSON.parse(raw));
  }

  private bookInvoice() {
    if (this.isDialogOpen) return;
    this.isDialogOpen = true;

    const salesItem = this.internalSalesItem();

    if (!salesItem) {
      this.isDialogOpen = false;
      return;
    }

    this.showPrintDialog(salesItem, false, async (printSettings) => {
      const spinnerData = { text: this.bookInvoiceLabel };
      const spinnerRef = this._dialog.open(KfmSpinnerDialogComponent, {
        data: spinnerData,
      });

      try {
        const result = await this._invoiceService.book(
          salesItem,
          printSettings,
        );

        if (result) {
          this.internalSalesItem.set(result);
          // await this._syncService.syncUploadOnly();

          spinnerData.text = 'PDF wird erstellt...';

          await this._printService.createInvoicePdf(
            result,
            printSettings,
            this._settings.showPrice,
            true,
            `Rechnung_${result.number}.pdf`,
          );
          this.salesItemBooked.emit(result);
        }
      } catch (err) {
        console.error('[SalesDetails] Fehler beim Buchen der Rechnung:', err);
        this._dialog.open(KfmInfoDialogComponent, {
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          data: {
            title: this.bookInvoiceErrorTitle,
            text: this.bookInvoiceErrorText,
          },
        });
      } finally {
        spinnerRef.close();
      }
    });
  }

  private async finishTask() {
    const salesItem = this.internalSalesItem();

    if (!salesItem) return;

    if (await this._taskService.finish(salesItem.uId))
      this.salesItemDeselected.emit();
  }

  protected async onPrintTaskClicked(): Promise<void> {
    if (this.isDialogOpen) return;
    const salesItem = this.internalSalesItem();
    if (!salesItem) return;

    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<PrintSettings>(
      KfmPrintDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: { processType: KfmProcessType.Order },
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        await this._printService.createTaskPdf(
          salesItem.uId,
          result,
          this._settings.showPrice,
        );
      }
      this.isDialogOpen = false;
    });
  }

  protected onDeleteTaskClicked(): void {
    if (this.isDialogOpen) return;
    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        title: this.deleteTaskLabel,
        text: this.deleteTaskTextLabel,
      },
    });

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        const salesItem = this.internalSalesItem();

        if (!salesItem) return;

        if (await this._taskService.delete(salesItem.uId)) {
          this.salesItemDeleted.emit();
        }
      }
      this.isDialogOpen = false;
    });
  }
}
